/**
 * Wraps React.lazy() with automatic retry logic for failed dynamic imports.
 *
 * Handles two common failure scenarios:
 * 1. Vite HMR invalidation in dev — stale `?t=` timestamps cause 404s
 * 2. New deployment in prod — browser has old chunk URLs cached
 *
 * On persistent failure (all retries exhausted) the rejected promise propagates
 * to the nearest RouteErrorBoundary which offers a manual page reload.
 */
import { lazy } from 'react';

type Factory<T extends React.ComponentType<any>> = () => Promise<{ default: T }>;

export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: Factory<T>,
  retries = 3,
  delayMs = 400,
): React.LazyExoticComponent<T> {
  return lazy(
    () =>
      new Promise<{ default: T }>((resolve, reject) => {
        const attempt = (triesLeft: number) => {
          factory()
            .then(resolve)
            .catch((err) => {
              if (triesLeft <= 0) {
                reject(err);
                return;
              }
              // Back-off before retrying
              setTimeout(() => attempt(triesLeft - 1), delayMs);
            });
        };
        attempt(retries);
      }),
  );
}

/** Returns true if the error is a dynamic-import / chunk-load failure */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('error loading dynamically imported module') ||
    error.name === 'ChunkLoadError'
  );
}
